import styled from '@emotion/styled';
import { ThemeProps, type Theme as ThemeType } from '@emotion/react';
import SiteTitle from '../Layout/Header/SiteTitle';
import ArrowDown from '../svgs/ArrowDown';
import SinglePageDateLocationLockup from './SinglePageDateLocationLockup';
import RSVPButton from '../RSVPButton';
import { useContext } from 'react';
import { WWContext, WWContextValue } from '../WWContext';
import useThemeStyles from '../../lib/useThemeStyles';
import { isWwsWeddingWebsiteV2, Wws_Page } from '../../types/graphql';
import Announcement from '../Layout/Announcement';
import { SiteSubtitleWithDateLocation } from '../Layout/Header/SiteSubtitle';
import useDateDisplayWithFormat from '../../lib/useDateDisplayWithFormat';
import InitialsFlourish from '../Layout/Header/InitialsFlourish';
import { getSectionId } from '../Templates/SinglePageTemplate';
import ThemeNextImage from '../NextImage/ThemeNextImage';
import { buildEditBasicInfoContextMenuButtons } from '../InlineEdit/InlineEditMessageFactory';
import InlineEditHandler from '../InlineEdit/InlineEditHandler/InlineEditHandler';

const TopImageWrap = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledSiteTitle = styled(SiteTitle)`
  text-align: center;
  padding-left: 16px;
  padding-right: 16px;
`;

const SplashPageWrapper = styled('div')`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 1024px) {
    height: auto;
    min-height: 50vh;
  }
`;

const SplashPageContent = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  margin-bottom: 10%;
  gap: 32px;
  padding: 0 16px;

  @media (max-width: 1024px) {
    gap: 16px;
  }
`;

const ScrollDownButton = styled('button')`
  padding: 16px;
`;

const StyledArrowDown = styled(ArrowDown)`
  width: 32px;
  height: 32px;
  fill: ${(props: ThemeProps): string | undefined =>
    props.theme.colors.primary};
`;

export const SinglePageSplashPageData = {
  id: 1,
  type: 'SinglePageSplashPage',
  title: 'Home',
  items: [],
  show: true,
  routeName: '',
} as unknown as Wws_Page;

interface SinglePageSplashScreenProps {
  onScrollDown?: () => void;
  themeOverride?: string | undefined;
  isMobile?: boolean;
}

const SinglePageSplashScreen = ({
  themeOverride,
  onScrollDown,
  isMobile = false,
}: SinglePageSplashScreenProps) => {
  const { data } = useContext<WWContextValue>(WWContext);

  const formattedDate = useDateDisplayWithFormat();

  const theme: Partial<ThemeType> = useThemeStyles(themeOverride);

  if (!isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    return null;
  }

  const topImage = theme.images?.top;

  return (
    <SplashPageWrapper
      id={getSectionId(SinglePageSplashPageData)}
      data-testid={getSectionId(SinglePageSplashPageData)}
    >
      <Announcement
        announcement={data?.weddingWebsiteResult?.announcement ?? undefined}
      />
      <TopImageWrap>
        <ThemeNextImage
          id="top-image"
          src={topImage || ''}
          alt="Top Theme Image"
          mobileSrc={theme.responsiveImages?.top?.smUrl || ''}
          loading="eager"
          priority
          imageCSSObject={theme.components?.TopImage}
          isTopImage
          desktopSizes="(min-width: 1024px) 40vw, (min-width: 768px) 100vw, 0vw"
          mobileSizes="(max-width: 767px) 100vw, 0vw"
        />
      </TopImageWrap>
      {theme.config?.displayInitialsLockup && (
        <InitialsFlourish
          firstName={data?.weddingWebsiteResult?.firstName}
          fianceFirstName={data.weddingWebsiteResult?.fianceFirstName}
          themeId={data.weddingWebsiteResult.theme?.id}
        />
      )}
      <StyledSiteTitle
        weddingWebsiteResult={data.weddingWebsiteResult}
        heroVersion={false}
      />

      <SplashPageContent>
        {isMobile && (
          <>
            <InlineEditHandler
              contextMenuButtons={buildEditBasicInfoContextMenuButtons()}
            >
              <SiteSubtitleWithDateLocation
                data-testid="header-wedding-date-location"
                heroVersion={false}
                weddingDate={formattedDate}
                weddingLocation={data?.weddingWebsiteResult?.weddingLocation}
                countdownDays={data?.weddingWebsiteResult?.countdownDays}
              />
            </InlineEditHandler>
            <RSVPButton />
          </>
        )}
        {!isMobile && (
          <>
            <InlineEditHandler
              contextMenuButtons={buildEditBasicInfoContextMenuButtons()}
              outlineOffset={2}
            >
              <SinglePageDateLocationLockup />
            </InlineEditHandler>
            <RSVPButton />
            <ScrollDownButton
              onClick={onScrollDown}
              data-testid="scroll-down-button"
            >
              <StyledArrowDown />
            </ScrollDownButton>
          </>
        )}
      </SplashPageContent>
    </SplashPageWrapper>
  );
};

export default SinglePageSplashScreen;
